import React from 'react';
import Chip from 'ha/ui/next/Chip';

import { useIntl } from 'ha/i18n';

import { CanonicalRegistration } from 'ha/constants/Filters';
import * as SearchFilters from 'ha/types/SearchFilters';

import { priceRange } from 'ha/utils/formatRanges';
import Grid from 'ha/ui/next/Grid';

interface Props {
  types?: SearchFilters.TypeFilterValue;
  furniture?: SearchFilters.FurnitureFilterValue;
  bills?: SearchFilters.BillsFilterValue;
  registration?: SearchFilters.RegistrationFilterValue;
  suitableFor?: SearchFilters.SuitableForFilterValue;
  recentlyAdded?: SearchFilters.RecentlyAddedFilterValue;
  rules?: SearchFilters.RulesFilterValue;
  gender?: SearchFilters.GenderFilterValue;
  rooms?: SearchFilters.RoomsFilterValue;
  rate?: number;
  price?: SearchFilters.PriceFilterChange | null;
  onRemove: (filter: string, value?: string) => void;
}

type Category =
  | 'type'
  | 'furniture'
  | 'bills'
  | 'suitableFor'
  | 'rules'
  | 'registration'
  | 'gender'
  | 'recentlyAdded';
type Value =
  | SearchFilters.TypeValue
  | SearchFilters.FurnitureValue
  | SearchFilters.BillsValue
  | SearchFilters.SuitableForValue
  | SearchFilters.RulesValue
  | SearchFilters.RegistrationValue
  | SearchFilters.GenderValue
  | SearchFilters.RecentlyAddedValue;

interface Tag {
  category: Category;
  value: Value;
  label: string;
}

export const TagList: Tag[] = [
  {
    category: 'type',
    value: SearchFilters.TypeValue.APARTMENT,
    label: 'Apartment',
  },
  {
    category: 'type',
    value: SearchFilters.TypeValue.PRIVATE_ROOM,
    label: 'Private Room',
  },
  {
    category: 'type',
    value: SearchFilters.TypeValue.SHARED_ROOM,
    label: 'Shared Room',
  },
  {
    category: 'type',
    value: SearchFilters.TypeValue.STUDIO,
    label: 'Studio',
  },
  {
    category: 'furniture',
    value: SearchFilters.FurnitureValue.FURNISHED,
    label: 'Furnished',
  },
  {
    category: 'furniture',
    value: SearchFilters.FurnitureValue.UNFURNISHED,
    label: 'Unfurnished',
  },
  {
    category: 'bills',
    value: SearchFilters.BillsValue.INCLUDED,
    label: 'Bills Included',
  },
  {
    category: 'bills',
    value: SearchFilters.BillsValue.EXCLUDED,
    label: 'Bills Excluded',
  },
  {
    category: 'suitableFor',
    value: SearchFilters.SuitableForValue.STUDENTS,
    label: 'Suitable for students',
  },
  {
    category: 'suitableFor',
    value: SearchFilters.SuitableForValue.WORKING_PROFESSIONALS,
    label: 'Suitable for working professionals',
  },
  {
    category: 'suitableFor',
    value: SearchFilters.SuitableForValue.COUPLES,
    label: 'Suitable for couples',
  },
  {
    category: 'rules',
    value: SearchFilters.RulesValue.SMOKING,
    label: 'Smoking Allowed',
  },
  {
    category: 'rules',
    value: SearchFilters.RulesValue.PETS,
    label: 'Pets Allowed',
  },
  {
    category: 'rules',
    value: SearchFilters.RulesValue.INSTRUMENT,
    label: 'Playing Instrument Allowed',
  },
  {
    category: 'registration',
    value: CanonicalRegistration.Possible,
    label: 'Registration Possible',
  },
  {
    category: 'registration',
    value: CanonicalRegistration.NotPossible,
    label: 'Registration Not Possible',
  },
  {
    category: 'gender',
    value: SearchFilters.GenderValue.BOYS,
    label: 'Shared with Male',
  },
  {
    category: 'gender',
    value: SearchFilters.GenderValue.GIRLS,
    label: 'Shared with Female',
  },
  {
    category: 'recentlyAdded',
    value: '1-day',
    label: 'Last 24 hours',
  },
  {
    category: 'recentlyAdded',
    value: '3-days',
    label: 'Last 3 days',
  },
  {
    category: 'recentlyAdded',
    value: '7-days',
    label: 'Last 7 days',
  },
  {
    category: 'recentlyAdded',
    value: '14-days',
    label: 'Last 14 days',
  },
];

export const FiltersTags: React.FC<Props> = ({
  types = [],
  furniture,
  bills,
  suitableFor = [],
  registration,
  rules = [],
  recentlyAdded,
  gender = [],
  rooms = {
    bedroomCount: [],
  },
  price,
  rate,
  onRemove,
}) => {
  const { T } = useIntl();

  return (
    <Grid container spacing={1}>
      {TagList.map(
        ({ category, value, label }, index) =>
          [
            ...types,
            furniture,
            bills,
            ...suitableFor,
            ...rules,
            registration,
            ...gender,
            recentlyAdded,
          ].includes(value) && (
            <Grid item key={index}>
              <Chip
                variant="outlined"
                onDelete={() => onRemove(category, value)}
                label={T(label)}
                data-test-locator={`FiltersTags ${value} Tag`}
              />
            </Grid>
          ),
      )}

      {rooms.bedroomCount.map((val, index) => (
        <Grid item key={index}>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('bedroomCount', val)}
            label={
              val === SearchFilters.RoomsValue.ONE
                ? T('%s bedroom', val)
                : T('%s bedrooms', val)
            }
            data-test-locator="FiltersTags Rooms Tag"
          />
        </Grid>
      ))}

      {price && rate && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('price')}
            label={priceRange(
              price.currency,
              price.min * rate,
              price.max * rate,
            )}
            data-test-locator="FiltersTags Price Tag"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default FiltersTags;
